"use client";
import Image, { StaticImageData } from "next/image";
import { useTranslations, useFormatter } from "next-intl";

import Slider from "@/shared/components/Slider";
import Video from "@/shared/components/Video";

import UsersImage from "@public/mini_slider_users.png";

import holdersIcon from "@public/holders_icon.png";
import usersIcon from "@public/users_icon.png";
import burnedIcon from "@public/burned_icon.png";

import s from "./styles.module.css";

const UsageMiniSlide = ({
  icon,
  title,
  subtitle,
  text,
  image,
  video,
}: {
  image?: StaticImageData;
  icon: StaticImageData;
  title: string;
  subtitle: string;
  text: string;
  video?: string;
}) => {
  return (
    <div className={s.usageMiniSlide}>
      <div className={s.videoContainer}>
        {video && <Video src={video} />}
        {image && (
          <Image
            fill
            className={s.leadingImage}
            src={image}
            alt={`Image for: ${title}`}
          />
        )}
      </div>
      <div>
        <Image src={icon} alt={`Image icon for ${title}`} className={s.icon} />
        <h3 className={s.usageMiniSlideTitle}>{title}</h3>
        <p className={s.usageMiniSlideSubtitle}>{subtitle}</p>
        <p className={s.usageMiniSlideDescription}>{text}</p>
      </div>
    </div>
  );
};

const SLIDE_COUNT = 3;

export default function TokenMiniSlider() {
  const t = useTranslations("Token");
  const f = useFormatter();

  // Each slide is min-width: 100vw, so the track width depends on the viewport.
  // Only ever reaches GSAP, never the rendered markup, so the server's 0 cannot
  // cause a hydration mismatch. Computed during render rather than in an effect
  // so Slider's useGSAP sets up its ScrollTrigger on mount, alongside the
  // page's other sliders.
  const width =
    typeof window === "undefined" ? 0 : window.innerWidth * SLIDE_COUNT;

  return (
    <Slider
      width={width}
      showController={false}
      regularPadding={false}
      start="top"
      elements={[
        <UsageMiniSlide
          key="0"
          video="/holders.mp4"
          icon={holdersIcon}
          title={t("holdersMiniSlideTitle")}
          subtitle={t("holdersMiniSlideSubtitle")}
          text={t("holdersMiniSlideDescription")}
        />,
        <UsageMiniSlide
          key="1"
          image={UsersImage}
          icon={usersIcon}
          title={t("usersMiniSlideTitle")}
          subtitle={t("usersMiniSlideSubtitle")}
          text={t("usersMiniSlideDescription")}
        />,
        <UsageMiniSlide
          key="2"
          video="/burn.mp4"
          icon={burnedIcon}
          title={t("burnedMiniSlideTitle", {
            billionAmount: f.number(3.359, {
              minimumFractionDigits: 3,
              maximumFractionDigits: 3,
            }),
          })}
          subtitle={t("burnedMiniSlideSubtitle")}
          text={t("burnedMiniSlideDescription")}
        />,
      ]}
    />
  );
}
